import { normalizePaint } from './paintUtils';

const clamp = (value, min, max, fallback) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return fallback;
  }
  return Math.min(Math.max(Number(value), min), max);
};

const normalizeColor = (value, fallback) => (
  typeof value === 'string' && value.trim().length > 0 ? value : fallback
);

export const DEFAULT_PAGE_SETTINGS = {
  backgroundPaint: normalizePaint({ type: 'solid', color: '#FFFFFF', alpha: 100 }, '#FFFFFF'),
  backgroundImage: {
    enabled: false,
    assetId: '',
    src: '',
    naturalW: 1200,
    naturalH: 1200,
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    opacity: 100,
  },
  background: {
    type: 'solid',
    color: '#FFFFFF',
    alpha: 100,
    gradient: {
      kind: 'linear',
      angle: 0,
      stops: [
        { color: '#FFFFFF', pos: 0 },
        { color: '#111111', pos: 100 },
      ],
    },
  },
  bleed: 0,
  margin: 0,
  showBleed: false,
  showMargin: true,
  showGrid: false,
  grid: {
    size: 50,
    type: 'dots',
    opacity: 0.2,
  },
  showRulers: true,
  showGuides: true,
  guides: {
    vertical: [],
    horizontal: [],
  },
  snap: {
    enabled: true,
    threshold: 6,
    toGrid: true,
    toGuides: true,
    toCenter: true,
    toEdges: true,
    toMargin: true,
  },
};

export const normalizePageSettings = (settings = {}) => {
  const background = settings.background || {};
  const gradient = background.gradient || {};
  const rawStops = Array.isArray(gradient.stops) ? gradient.stops : [];
  const stops = rawStops.length
    ? rawStops
        .map((stop) => ({
          color: normalizeColor(stop?.color, '#FFFFFF'),
          pos: clamp(stop?.pos, 0, 100, 0),
        }))
        .sort((a, b) => a.pos - b.pos)
    : DEFAULT_PAGE_SETTINGS.background.gradient.stops;

  const backgroundPaint = (() => {
    if (settings.backgroundPaint) {
      return normalizePaint(settings.backgroundPaint, DEFAULT_PAGE_SETTINGS.background.color);
    }
    if (background.type === 'gradient') {
      return normalizePaint({
        type: 'gradient',
        kind: gradient.kind === 'radial' ? 'radial' : 'linear',
        angle: clamp(gradient.angle, 0, 360, DEFAULT_PAGE_SETTINGS.background.gradient.angle),
        stops: stops.map((stop) => ({
          color: stop.color,
          alpha: clamp(background.alpha, 0, 100, DEFAULT_PAGE_SETTINGS.background.alpha),
          pos: stop.pos,
        })),
      }, DEFAULT_PAGE_SETTINGS.background.color);
    }
    return normalizePaint({
      type: 'solid',
      color: normalizeColor(background.color, DEFAULT_PAGE_SETTINGS.background.color),
      alpha: clamp(background.alpha, 0, 100, DEFAULT_PAGE_SETTINGS.background.alpha),
    }, DEFAULT_PAGE_SETTINGS.background.color);
  })();

  const normalizeBackgroundImage = (value = {}) => {
    const src = typeof value?.src === 'string' ? value.src : '';
    const enabled = Boolean(value?.enabled && src);
    return {
      enabled,
      assetId: typeof value?.assetId === 'string' ? value.assetId : '',
      src,
      naturalW: clamp(value?.naturalW, 1, 20000, DEFAULT_PAGE_SETTINGS.backgroundImage.naturalW),
      naturalH: clamp(value?.naturalH, 1, 20000, DEFAULT_PAGE_SETTINGS.backgroundImage.naturalH),
      scale: clamp(value?.scale, 0.01, 20, DEFAULT_PAGE_SETTINGS.backgroundImage.scale),
      offsetX: clamp(value?.offsetX, -100000, 100000, DEFAULT_PAGE_SETTINGS.backgroundImage.offsetX),
      offsetY: clamp(value?.offsetY, -100000, 100000, DEFAULT_PAGE_SETTINGS.backgroundImage.offsetY),
      opacity: clamp(value?.opacity, 0, 100, DEFAULT_PAGE_SETTINGS.backgroundImage.opacity),
    };
  };

  return {
    backgroundPaint,
    backgroundImage: normalizeBackgroundImage(settings.backgroundImage),
    background: {
      type: background.type === 'gradient' ? 'gradient' : 'solid',
      color: normalizeColor(background.color, DEFAULT_PAGE_SETTINGS.background.color),
      alpha: clamp(background.alpha, 0, 100, DEFAULT_PAGE_SETTINGS.background.alpha),
      gradient: {
        kind: gradient.kind === 'radial' ? 'radial' : 'linear',
        angle: clamp(gradient.angle, 0, 360, DEFAULT_PAGE_SETTINGS.background.gradient.angle),
        stops,
      },
    },
    bleed: clamp(settings.bleed, 0, 500, DEFAULT_PAGE_SETTINGS.bleed),
    margin: clamp(settings.margin, 0, 500, DEFAULT_PAGE_SETTINGS.margin),
    showBleed: settings.showBleed === undefined
      ? DEFAULT_PAGE_SETTINGS.showBleed
      : Boolean(settings.showBleed),
    showMargin: settings.showMargin === undefined
      ? DEFAULT_PAGE_SETTINGS.showMargin
      : Boolean(settings.showMargin),
    showGrid: settings.showGrid === undefined
      ? DEFAULT_PAGE_SETTINGS.showGrid
      : Boolean(settings.showGrid),
    grid: {
      size: clamp(settings.grid?.size, 8, 400, DEFAULT_PAGE_SETTINGS.grid.size),
      type: settings.grid?.type === 'lines' ? 'lines' : 'dots',
      opacity: clamp(settings.grid?.opacity, 0.05, 0.8, DEFAULT_PAGE_SETTINGS.grid.opacity),
    },
    showRulers: settings.showRulers === undefined
      ? DEFAULT_PAGE_SETTINGS.showRulers
      : Boolean(settings.showRulers),
    showGuides: settings.showGuides === undefined
      ? DEFAULT_PAGE_SETTINGS.showGuides
      : Boolean(settings.showGuides),
    guides: {
      vertical: Array.isArray(settings.guides?.vertical)
        ? settings.guides.vertical.map((value) => clamp(value, -10000, 100000, 0))
        : [],
      horizontal: Array.isArray(settings.guides?.horizontal)
        ? settings.guides.horizontal.map((value) => clamp(value, -10000, 100000, 0))
        : [],
    },
    snap: {
      enabled: settings.snap?.enabled === undefined
        ? DEFAULT_PAGE_SETTINGS.snap.enabled
        : Boolean(settings.snap.enabled),
      threshold: clamp(settings.snap?.threshold, 2, 20, DEFAULT_PAGE_SETTINGS.snap.threshold),
      toGrid: settings.snap?.toGrid === undefined
        ? DEFAULT_PAGE_SETTINGS.snap.toGrid
        : Boolean(settings.snap.toGrid),
      toGuides: settings.snap?.toGuides === undefined
        ? DEFAULT_PAGE_SETTINGS.snap.toGuides
        : Boolean(settings.snap.toGuides),
      toCenter: settings.snap?.toCenter === undefined
        ? DEFAULT_PAGE_SETTINGS.snap.toCenter
        : Boolean(settings.snap.toCenter),
      toEdges: settings.snap?.toEdges === undefined
        ? DEFAULT_PAGE_SETTINGS.snap.toEdges
        : Boolean(settings.snap.toEdges),
      toMargin: settings.snap?.toMargin === undefined
        ? DEFAULT_PAGE_SETTINGS.snap.toMargin
        : Boolean(settings.snap.toMargin),
    },
  };
};

export const mergePageSettings = (current, patch) => {
  const merged = {
    ...current,
    ...patch,
    backgroundPaint: patch.backgroundPaint
      ? normalizePaint(patch.backgroundPaint, current.backgroundPaint?.color || DEFAULT_PAGE_SETTINGS.background.color)
      : current.backgroundPaint,
    backgroundImage: patch.backgroundImage
      ? {
          ...(current.backgroundImage || DEFAULT_PAGE_SETTINGS.backgroundImage),
          ...patch.backgroundImage,
        }
      : current.backgroundImage,
    background: {
      ...current.background,
      ...(patch.background || {}),
      gradient: {
        ...current.background?.gradient,
        ...(patch.background?.gradient || {}),
      },
    },
    grid: {
      ...current.grid,
      ...(patch.grid || {}),
    },
    guides: {
      ...current.guides,
      ...(patch.guides || {}),
    },
    snap: {
      ...current.snap,
      ...(patch.snap || {}),
    },
  };
  return normalizePageSettings(merged);
};

const STORAGE_KEY = 'studio.pageSettingsDefault';

export const getStoredPageSettingsDefault = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizePageSettings(parsed);
  } catch (error) {
    return null;
  }
};

export const setStoredPageSettingsDefault = (settings) => {
  try {
    const normalized = normalizePageSettings(settings);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  } catch (error) {
    // ignore storage failures
  }
};
